import { currencyCodes } from '../config';
import type {
  AppSettings,
  QuoteStatus,
  QuoteStoreExportData,
  QuoteStoreExportQuote,
  SavedOffer,
  StorageCounters,
} from '../types';

const EXPORT_VERSION = 'service-laama-quotestore-v1' as const;
const EXPORTED_BY = 'Halton Service Laama v2.0' as const;
const COUNTERS_STORAGE_KEY = 'halton_v2_counters';
const QUOTE_DAILY_COUNTERS_STORAGE_KEY = 'halton_v2_quote_daily_counters';
const QUOTE_COUNTER_LOCK_NAME = 'service-laama-quote-counters';

type BrowserLocks = {
  request<T>(name: string, callback: () => T | Promise<T>): Promise<T>;
};

function createDefaultCounters(): StorageCounters {
  return {
    offerCounter: 1,
    workOrderCounter: 1,
    maintenanceReportCounter: 1,
    ansulCommissioningCounter: 1,
    jobNumberCounter: 1,
    jobNumberYear: new Date().getFullYear(),
    quoteDailyCounters: {},
  };
}

function readPositiveInteger(value: unknown, fallback: number): number {
  if (value === undefined) {
    return fallback;
  }
  if (!Number.isInteger(value) || Number(value) < 1) {
    throw new Error('Counter value must be a positive integer');
  }
  return Number(value);
}

function readQuoteDailyCounters(value: unknown): Record<string, number> {
  if (value === undefined) {
    return {};
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Quote daily counters must be an object');
  }

  return Object.entries(value).reduce<Record<string, number>>((acc, [key, counter]) => {
    if (!Number.isInteger(counter) || Number(counter) < 0) {
      throw new Error(`Quote daily counter "${key}" must be a non-negative integer`);
    }
    acc[key] = Number(counter);
    return acc;
  }, {});
}

export function validateImportedQuoteCounters(value: unknown): {
  sharedCounters: StorageCounters;
  quoteDailyCounters?: Record<string, number>;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Counter data must be an object');
  }

  const defaults = createDefaultCounters();
  const storedCounters = value as Record<string, unknown>;
  const quoteDailyCounters = storedCounters.quoteDailyCounters === undefined
    ? undefined
    : readQuoteDailyCounters(storedCounters.quoteDailyCounters);

  return {
    sharedCounters: {
      offerCounter: readPositiveInteger(storedCounters.offerCounter, defaults.offerCounter),
      workOrderCounter: readPositiveInteger(storedCounters.workOrderCounter, defaults.workOrderCounter),
      maintenanceReportCounter: readPositiveInteger(storedCounters.maintenanceReportCounter, defaults.maintenanceReportCounter),
      ansulCommissioningCounter: readPositiveInteger(storedCounters.ansulCommissioningCounter, defaults.ansulCommissioningCounter),
      jobNumberCounter: readPositiveInteger(storedCounters.jobNumberCounter, defaults.jobNumberCounter),
      jobNumberYear: readPositiveInteger(storedCounters.jobNumberYear, defaults.jobNumberYear),
    },
    quoteDailyCounters,
  };
}

export function loadQuoteCounters(): StorageCounters {
  const defaults = createDefaultCounters();

  try {
    const stored = localStorage.getItem(COUNTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Counter data must be an object');
      }
      const storedCounters = parsed as Record<string, unknown>;
      return {
        offerCounter: readPositiveInteger(storedCounters.offerCounter, defaults.offerCounter),
        workOrderCounter: readPositiveInteger(storedCounters.workOrderCounter, defaults.workOrderCounter),
        maintenanceReportCounter: readPositiveInteger(storedCounters.maintenanceReportCounter, defaults.maintenanceReportCounter),
        ansulCommissioningCounter: readPositiveInteger(storedCounters.ansulCommissioningCounter, defaults.ansulCommissioningCounter),
        jobNumberCounter: readPositiveInteger(storedCounters.jobNumberCounter, defaults.jobNumberCounter),
        jobNumberYear: readPositiveInteger(storedCounters.jobNumberYear, defaults.jobNumberYear),
        quoteDailyCounters: readQuoteDailyCounters(storedCounters.quoteDailyCounters),
      };
    }
  } catch (error) {
    console.error('Failed to load quote counters:', error);
    throw new Error('Failed to load quote counters');
  }

  return defaults;
}

export function saveQuoteCounters(counters: StorageCounters): void {
  saveQuoteDailyCountersForBackup(counters.quoteDailyCounters || {});
}

export function saveQuoteDailyCountersForBackup(counters: unknown): void {
  try {
    localStorage.setItem(
      QUOTE_DAILY_COUNTERS_STORAGE_KEY,
      JSON.stringify(readQuoteDailyCounters(counters))
    );
  } catch (error) {
    console.error('Failed to save quote counters:', error);
    throw new Error('Failed to save quote counters');
  }
}

export function loadQuoteDailyCountersForBackup(): Record<string, number> {
  const legacyCounters = loadQuoteCounters().quoteDailyCounters || {};
  return loadStoredQuoteDailyCounters(legacyCounters);
}

function loadStoredQuoteDailyCounters(
  legacyCounters: Record<string, number>
): Record<string, number> {
  try {
    const stored = localStorage.getItem(QUOTE_DAILY_COUNTERS_STORAGE_KEY);
    if (stored === null) {
      return { ...legacyCounters };
    }
    return readQuoteDailyCounters(JSON.parse(stored) as unknown);
  } catch (error) {
    console.error('Failed to load quote daily counters:', error);
    throw new Error('Failed to load quote counters');
  }
}

function getBrowserLocks(): BrowserLocks | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return (navigator as Navigator & { locks?: BrowserLocks }).locks;
}

export function withQuoteCounterLock<T>(operation: () => T): Promise<T> {
  const locks = getBrowserLocks();
  if (!locks) {
    return Promise.reject(new Error('Web Locks API is required to generate quote numbers'));
  }

  return locks.request(QUOTE_COUNTER_LOCK_NAME, operation);
}

export function normalizeInitials(value: string): string {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const parts = trimmed.split(/\s+/);

  if (parts.length >= 2) {
    // First char of first and first char of last
    const first = parts[0].charAt(0);
    const last = parts[parts.length - 1].charAt(0);
    const combined = (first + last).toUpperCase();
    // Only return if both characters are letters (including Finnish letters)
    if (/[a-zåäöA-ZÅÄÖ]/.test(first) && /[a-zåäöA-ZÅÄÖ]/.test(last)) {
      return combined;
    }
  }

  // Strip non-letters (including support for Finnish letters åäöÅÄÖ)
  const letters = trimmed.replace(/[^a-zåäöA-ZÅÄÖ]/g, '');
  if (letters.length === 0) {
    return '';
  }

  // Take first two letters and uppercase
  return letters.substring(0, 2).toUpperCase();
}

function generateQuoteNumberInLock(initials: string, date = new Date()): string {
  const normalized = normalizeInitials(initials);
  if (!normalized) {
    throw new Error('User initials are required to generate a quote number');
  }

  // Format date as ddMMyy using local date parts
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const ddMMyy = `${day}${month}${year}`;

  // Create daily sequence key using local calendar date (not UTC)
  const fullYear = date.getFullYear();
  const localMonth = String(date.getMonth() + 1).padStart(2, '0');
  const localDay = String(date.getDate()).padStart(2, '0');
  const dailyKey = `${normalized}-${fullYear}-${localMonth}-${localDay}`;

  // Load counters
  const counters = loadQuoteCounters();
  counters.quoteDailyCounters = loadStoredQuoteDailyCounters(counters.quoteDailyCounters || {});

  // Get next sequence number for this key
  const currentSeq = counters.quoteDailyCounters[dailyKey] || 0;
  const nextSeq = currentSeq + 1;

  // Save updated counters
  counters.quoteDailyCounters[dailyKey] = nextSeq;
  saveQuoteCounters(counters);

  // Return formatted quote number
  return `Q-${normalized}-${ddMMyy}-${String(nextSeq).padStart(2, '0')}`;
}

export function generateQuoteNumber(initials: string, date = new Date()): Promise<string> {
  return withQuoteCounterLock(() => generateQuoteNumberInLock(initials, date));
}

export function getOfferQuoteStatus(offer: SavedOffer): QuoteStatus {
  return offer.quoteStatus || 'draft';
}

export function mapOfferToQuoteStoreQuote(offer: SavedOffer): QuoteStoreExportQuote {
  const currency = currencyCodes[offer.settings.companyCountry];

  return {
    laamaId: offer.id,
    quoteNumber: offer.documentNumber,
    internalAccountId: offer.internalAccountId || '',
    siteLocationId: offer.siteLocationId || '',
    customerName: offer.customerName,
    vatId: offer.vatId || '',
    subtotal: offer.totals.subtotal,
    validityDays: offer.validityDays || null,
    status: getOfferQuoteStatus(offer),
    createdAt: offer.createdAt,
    updatedAt: offer.updatedAt,
    currency,
    payload: offer,
  };
}

export function buildQuoteStoreExport(
  offers: SavedOffer[],
  metadata?: { exportId?: string; exportedAt?: string }
): QuoteStoreExportData {
  const exportId = metadata?.exportId || crypto.randomUUID();
  const exportedAt = metadata?.exportedAt || new Date().toISOString();

  const quotes = offers.map(mapOfferToQuoteStoreQuote);

  return {
    exportVersion: EXPORT_VERSION,
    exportId,
    exportedAt,
    exportedBy: EXPORTED_BY,
    quotes,
  };
}

export function validateQuoteStoreExport(data: QuoteStoreExportData): string[] {
  const errors: string[] = [];

  if (data.exportVersion !== EXPORT_VERSION) {
    errors.push(`exportVersion must be ${EXPORT_VERSION}`);
  }

  if (!data.exportId) {
    errors.push('exportId is required');
  }

  if (!data.exportedAt) {
    errors.push('exportedAt is required');
  }

  // Validate each quote
  data.quotes.forEach((quote, index) => {
    if (!quote.laamaId) {
      errors.push(`quotes[${index}].laamaId is required`);
    }
    if (!quote.quoteNumber) {
      errors.push(`quotes[${index}].quoteNumber is required`);
    }
    if (!quote.customerName) {
      errors.push(`quotes[${index}].customerName is required`);
    }
    if (!quote.status) {
      errors.push(`quotes[${index}].status is required`);
    }
    if (!quote.createdAt) {
      errors.push(`quotes[${index}].createdAt is required`);
    }
  });

  return errors;
}

export function exportQuoteStoreOffers(offers: SavedOffer[]): void {
  const exportData = buildQuoteStoreExport(offers);
  const errors = validateQuoteStoreExport(exportData);

  if (errors.length > 0) {
    throw new Error(`Export validation failed:\n${errors.join('\n')}`);
  }

  // Generate filename with current date
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const filename = `quotestore-export-${dateStr}.json`;

  // Create blob and trigger download
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getQuoteInitialsFromSettings(settings: AppSettings): string {
  return normalizeInitials(settings.userInitials);
}
